import React, { useMemo } from 'react';
import { ReportingTimeSeries } from '../../services/api';

/**
 * Lightweight inline-SVG bar chart for the accept/reject time series.
 * No external chart lib — keeps bundle size flat and the chart matches
 * the dashboard's hand-rolled visual style.
 *
 * Bars are stacked (accepts on top of rejects) per bucket. Tooltip on
 * hover via a simple title element — fine for a reporting overview.
 * If we ever need a richer chart (zoom, brush, overlay multiple series)
 * we'd swap to Chart.js, but for "what does the trend look like" this
 * is enough.
 */

interface Props {
  data: ReportingTimeSeries;
  height?: number;
  /** Org IANA timezone. Hour-bucket labels are rendered in this zone so the
   *  chart x-axis matches the account's local clock, not the viewer's browser. */
  tz?: string;
}

function fmtBucket(bucket: string, granularity: 'hour' | 'day', tz?: string): string {
  // Hour buckets arrive as ISO timestamps (UTC instants) — localize to the org
  // tz. Day buckets arrive as YYYY-MM-DD already in the org's local calendar
  // (the backend re-buckets via AT TIME ZONE), so we format the month/day
  // directly without any timezone conversion that could shift the date.
  if (granularity === 'hour') {
    const d = new Date(bucket);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', timeZone: tz });
  }
  return new Date(bucket + 'T00:00:00').toLocaleString(undefined, { month: 'short', day: 'numeric' });
}

export default function TimeSeriesChart({ data, height = 220, tz }: Props) {
  const { points, max } = useMemo(() => {
    const points = data.series;
    const max = points.reduce((m, p) => Math.max(m, p.accepts + p.rejects), 0);
    return { points, max };
  }, [data]);

  if (points.length === 0) {
    return (
      <div style={{
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#9ca3af',
        background: '#f9fafb',
        border: '1px dashed #e5e7eb',
        borderRadius: 8,
      }}>
        No data in this range
      </div>
    );
  }

  // Width auto-scales with bucket count; min 24px per bar so labels are readable.
  const barWidth = Math.max(24, Math.min(72, Math.floor(900 / points.length)));
  const gap = 4;
  const innerW = points.length * (barWidth + gap);
  const padLeft = 48;   // for y-axis labels
  const padBottom = 32; // for x-axis labels
  const chartW = padLeft + innerW + 8;
  const chartH = height;
  const plotH = chartH - padBottom;

  // 4 horizontal gridlines.
  const ticks = max === 0 ? [0] : [0, 0.25, 0.5, 0.75, 1].map(f => Math.round(f * max));
  const yFor = (v: number) => (max === 0 ? plotH : plotH - (v / max) * plotH);

  return (
    <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
      <svg width={chartW} height={chartH} style={{ display: 'block', minWidth: chartW }}>
        {/* y-axis grid + labels */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padLeft} y1={yFor(t)} x2={chartW - 8} y2={yFor(t)} stroke="#f3f4f6" strokeWidth={1} />
            <text x={padLeft - 8} y={yFor(t) + 4} textAnchor="end" fontSize={11} fill="#6b7280">
              {t.toLocaleString()}
            </text>
          </g>
        ))}

        {/* bars — rejects on bottom (red), accepts stacked on top (blue) */}
        {points.map((p, i) => {
          const x = padLeft + i * (barWidth + gap);
          const total = p.accepts + p.rejects;
          const acceptH = max === 0 ? 0 : (p.accepts / max) * plotH;
          const rejectH = max === 0 ? 0 : (p.rejects / max) * plotH;
          const showLabel = points.length <= 24 || i % Math.ceil(points.length / 12) === 0;
          return (
            <g key={p.bucket}>
              {/* reject (red) */}
              <rect
                x={x}
                y={plotH - rejectH}
                width={barWidth}
                height={rejectH}
                fill="#ef4444"
              >
                <title>{`${fmtBucket(p.bucket, data.granularity, tz)}\nRejects: ${p.rejects.toLocaleString()}`}</title>
              </rect>
              {/* accept (blue) — sits on top of reject */}
              <rect
                x={x}
                y={plotH - rejectH - acceptH}
                width={barWidth}
                height={acceptH}
                fill="#2563eb"
              >
                <title>{`${fmtBucket(p.bucket, data.granularity, tz)}\nAccepts: ${p.accepts.toLocaleString()}\nRejects: ${p.rejects.toLocaleString()}\nTotal: ${total.toLocaleString()}`}</title>
              </rect>
              {showLabel && (
                <text
                  x={x + barWidth / 2}
                  y={chartH - 10}
                  textAnchor="middle"
                  fontSize={10}
                  fill="#6b7280"
                >
                  {fmtBucket(p.bucket, data.granularity, tz)}
                </text>
              )}
            </g>
          );
        })}

        {/* baseline */}
        <line x1={padLeft} y1={plotH} x2={chartW - 8} y2={plotH} stroke="#d1d5db" strokeWidth={1} />
      </svg>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: '#6b7280' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ display: 'inline-block', width: 12, height: 12, background: '#2563eb', borderRadius: 2 }} />
          Accepts
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ display: 'inline-block', width: 12, height: 12, background: '#ef4444', borderRadius: 2 }} />
          Rejects
        </span>
        <span style={{ marginLeft: 'auto' }}>
          Granularity: {data.granularity === 'hour' ? 'Hourly' : 'Daily'}
        </span>
      </div>
    </div>
  );
}
